import { AutoComplete, Input } from 'antd';
import { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGooglePlacesAutocomplete } from 'app/hooks/useGooglePlacesAutocomplete';

interface AddressComponents {
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

interface AddressAutocompleteProps {
  value?: string;
  onChange?: (value: string) => void;
  onAddressSelect?: (components: AddressComponents) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const AddressAutocomplete = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = '',
  disabled = false,
}: AddressAutocompleteProps): JSX.Element => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState<string>(value || '');
  const { suggestions, isLoading, fetchSuggestions, getPlaceDetails, clearSuggestions } = useGooglePlacesAutocomplete();

  useEffect(() => {
    setSearchValue(value || '');
  }, [value]);

  const handleSearch = useCallback(
    (searchText: string) => {
      setSearchValue(searchText);
      onChange?.(searchText);

      if (searchText && searchText.length >= 3) {
        fetchSuggestions(searchText);
      } else {
        clearSuggestions();
      }
    },
    [fetchSuggestions, clearSuggestions, onChange]
  );

  const handleSelect = useCallback(
    async (selectedValue: string, option: { key: string }) => {
      const placeId = option.key;

      // Set the selected address description immediately
      setSearchValue(selectedValue);
      onChange?.(selectedValue);
      clearSuggestions();

      // Fetch detailed place information
      const addressComponents = await getPlaceDetails(placeId);

      if (addressComponents && onAddressSelect) {
        // Update the search value with the formatted address
        setSearchValue(addressComponents.address);
        onChange?.(addressComponents.address);

        // Call the callback with all address components
        onAddressSelect(addressComponents);
      }
    },
    [getPlaceDetails, onAddressSelect, clearSuggestions, onChange]
  );

  const options = suggestions.map(suggestion => ({
    value: suggestion.description,
    label: suggestion.description,
    key: suggestion.placeId,
  }));

  return (
    <AutoComplete
      value={searchValue}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      disabled={disabled}
      notFoundContent={isLoading ? t('common.loading') : t('common.noResultsFound')}
      style={{ width: '100%' }}
    >
      <Input placeholder={placeholder} disabled={disabled} />
    </AutoComplete>
  );
};
